/**
 * A simple wrapper around <input> that provides some basic styling.
 **/
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import * as tokens from "@khanacademy/wonder-blocks-tokens";
import {borderRadius} from "@repo/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

type DeprecatedIcon =
    typeof import("../../shared-styles-package/deprecated-icon.tsx").default;

type Props = {
    // This id is used to tie the input field to the label that describes it.
    // TODO(diedra): Instead of passing in this id to connect the label and input
    // field, we should pass in the label for the input field and place it
    // inside the <form>.
    id?: string;
    testId?: string;
    type?: string;
    name?: string;
    readOnly?: boolean;
    value?: string | null | undefined;
    placeholder?: string;
    // https://reactjs.org/docs/uncontrolled-components.html#default-values
    defaultValue?: string | null | undefined;
    disabled?: boolean;
    autoComplete?: string;
    autoFocus?: boolean;
    maxLength?: number;
    invalid?: "true" | "false";
    // styling
    // This prop is passed directly to <input>.  This is incorrect since HTML
    // tags don't know what to do with an aphrodite which is typed as:
    // {
    //   _len: number,
    //   _name: string,
    //   _definition: CSSProperties,
    // }
    style?: CSSProperties;
    className?: string;
    width?: number | string;
    grow?: boolean | number;
    shrink?: boolean | number;
    // HACK(mattmorgan) to enforce WB LabelMedium-like styles on this older
    // component.
    labelMediumInputText?: boolean;
    icon?: React.ReactElement<React.ComponentProps<DeprecatedIcon>>;
    leftSideIcon?: boolean;
    backgroundColor?: string;
    focusBorderColor?: string;
    borderColor?: string;
    color?: string;
    // events
    onBlur?: (arg1: React.FocusEvent<HTMLInputElement>) => unknown;
    onFocus?: (arg1: React.FocusEvent<HTMLInputElement>) => unknown;
    onChange?: (arg1: React.ChangeEvent<HTMLInputElement>) => unknown;
    onClick?: (arg1: React.MouseEvent<HTMLInputElement>) => unknown;
    onKeyPress?: (arg1: React.KeyboardEvent<HTMLInputElement>) => unknown;
    onKeyDown?: (arg1: React.KeyboardEvent<HTMLInputElement>) => unknown;
    // aria
    ["aria-label"]?: string;
    ["aria-describedby"]?: string | null | undefined;
};

type State = {
    focused: boolean;
};

type PropsWithForwardRef = Props & {
    forwardedRef: React.Ref<HTMLInputElement>;
};

/**
 * @deprecated Please use `TextField` from `@khanacademy/wonder-blocks-form` instead.
 */
class DeprecatedTextField extends React.Component<PropsWithForwardRef, State> {
    state: State = {
        focused: false,
    };

    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void = (e) => {
        const {onBlur} = this.props;
        onBlur && onBlur(e);

        this.setState({focused: false});
    };

    handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void = (e) => {
        const {onFocus} = this.props;
        onFocus && onFocus(e);

        this.setState({focused: true});
    };

    disableDefault: (e: Event) => void = (e) => {
        e.preventDefault();
    };

    render(): React.ReactNode {
        const {
            forwardedRef,
            width,
            grow,
            shrink,
            icon,
            backgroundColor,
            focusBorderColor,
            borderColor,
            color,
            leftSideIcon,
            id,
            testId,
            type,
            labelMediumInputText,
            value,
            defaultValue,
            "aria-describedby": ariaDescribedBy,
            ...allProps
        } = this.props;
        const {focused} = this.state;

        const extraStyles: any = {};
        const spanStyle = [styles.container];

        if (width) {
            extraStyles.width = width;
        }

        if (grow) {
            extraStyles.flexGrow = grow === true ? 1 : grow;
        }

        if (shrink || shrink === 0) {
            extraStyles.flexShrink = shrink === true ? 0 : shrink;
        }

        if (backgroundColor) {
            extraStyles.backgroundColor = backgroundColor;
        } else {
            spanStyle.push(styles.defaultBackground);
        }

        // Add text color if specified.
        if (color) {
            extraStyles.color = color;
        }

        const borderColorStyle = focused
            ? focusBorderColor || tokens.color.blue
            : borderColor || tokens.color.offBlack50;
        extraStyles.border = `1px solid ${borderColorStyle}`;

        const wrappedIcon = icon && (
            <span className={css(styles.icon)}>{icon}</span>
        );

        const inputBase = labelMediumInputText
            ? styles.labelMediumInputBase
            : styles.inputBase;

        // TODO(diedra): The label for the input field should be here inside of
        // the form.
        return (
            <View style={[spanStyle, extraStyles]}>
                {leftSideIcon && wrappedIcon}
                <input
                    {...allProps}
                    value={value ?? undefined}
                    defaultValue={defaultValue ?? undefined}
                    aria-describedby={ariaDescribedBy ?? undefined}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    type={type}
                    className={css(
                        inputBase,
                        allProps.disabled && styles.inputDisabled,
                    )}
                    ref={forwardedRef}
                    id={id}
                    data-testid={testId}
                />
                {!leftSideIcon && wrappedIcon}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    inputBase: {
        background: "inherit",
        border: "none",
        ...deprecatedTypography.bodyXsmall,
        outline: "none",
        "::placeholder": {
            color: tokens.color.offBlack64,
        },
        width: "100%",
        color: "inherit",
        marginBottom: 0,
        paddingLeft: 0,
        boxShadow: "none",
        padding: 0, // override Chrome's stylesheet
        margin: 0, // override Safari's stylesheet
    },
    inputDisabled: {
        background: "transparent",
    },
    // HACK(mattmorgan) to enforce WB LabelMedium-like styles on this older
    // component.
    labelMediumInputBase: {
        background: "inherit",
        border: "none",
        outline: "none",
        "::placeholder": {
            color: tokens.color.offBlack64,
        },
        width: "100%",
        fontWeight: "normal",
        fontFamily: "Lato, san-serif",
        fontSize: "16px",
        lineHeight: "20px",
    },
    container: {
        height: 40,
        borderRadius,
        boxSizing: "border-box",
        padding: "8px 10px",
        display: "inline-flex",
        alignItems: "center",
        marginBottom: 0,
    },
    defaultBackground: {
        backgroundColor: tokens.color.white,
    },
    icon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

export default React.forwardRef<HTMLInputElement, Props>((props, ref) => (
    <DeprecatedTextField {...props} forwardedRef={ref} />
));
